import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API, useAuth, formatApiErrorDetail } from "../App";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  MapPin,
  Star,
  Clock,
  Phone,
  Globe,
  Mail,
  Heart,
  Share2,
  Calendar as CalendarIcon,
  Users,
  Utensils,
  Check,
  X,
  Megaphone,
  Facebook,
  Instagram,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { formatDate } from "../utils/dateFormat";
import { useTranslation } from "react-i18next";
import { useLocalized } from "../utils/localized";
import { RatingChip } from "../components/Reviews";
import VenueHero from "./venue/VenueHero";
import VenueMenu from "./venue/VenueMenu";
import VenueReviews from "./venue/VenueReviews";
import "leaflet/dist/leaflet.css";

export default function VenueDetailPage() {
  const { districtSlug, areaSlug, venueSlug } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [venue, setVenue] = useState(null);
  const [events, setEvents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [openingHours, setOpeningHours] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    date: new Date(),
    time: "19:00",
    guests: "2",
    name: "",
    phone: "",
    email: "",
    specialRequest: "",
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const L = (field) => {
    if (!venue) return "";
    if (lang === "el") return venue[`${field}_el`] || venue[field] || "";
    return venue[field] || "";
  };

  // Phase 3: feature-toggle gating. Toggles default to ON when missing so
  // legacy/un-migrated venues stay fully visible. The enrichment endpoint
  // always returns the active plan's toggle dict for plan-aware venues.
  const can = (key) => {
    if (!venue || !venue.feature_toggles) return true;
    return venue.feature_toggles[key] !== false;
  };
  const [claimOpen, setClaimOpen] = useState(false);

  // Phase 4 (P3): resolved layout for the venue. Maps section_key -> visible.
  // The backend's `_apply_plan_gating` ANDs each section's layout `visible`
  // flag with the plan's feature_toggle, so a section the admin enabled in
  // the layout but disabled in the plan (or vice versa) comes back as
  // `visible: false`. This is the single source of truth for what to render.
  const [layoutMap, setLayoutMap] = useState(null);
  // Section keys in resolved order — used to compute flex `order` values for
  // each left-column block so admins can re-arrange the whole profile via
  // the layout builder without us touching JSX.
  const [layoutOrderKeys, setLayoutOrderKeys] = useState([]);
  useEffect(() => {
    if (!venue?.venue_id) return;
    let cancelled = false;
    axios.get(`${API}/venues/${venue.venue_id}/layout?layout_type=public`)
      .then((r) => {
        if (cancelled) return;
        const map = {};
        const order = [];
        (r.data?.sections || []).forEach((s) => {
          map[s.key] = s.visible !== false;
          order.push(s.key);
        });
        setLayoutMap(map);
        setLayoutOrderKeys(order);
      })
      .catch(() => { if (!cancelled) { setLayoutMap({}); setLayoutOrderKeys([]); } });
    return () => { cancelled = true; };
  }, [venue?.venue_id]);

  // Combined gate: if the layout has an explicit entry, it wins. Otherwise,
  // fall back to the plan-toggle check above. Both must allow rendering for
  // sections that have BOTH layout + plan-toggle keys (`reviews`, `menu`...).
  const showSection = (layoutKey, toggleKey) => {
    if (layoutMap && Object.prototype.hasOwnProperty.call(layoutMap, layoutKey)) {
      return layoutMap[layoutKey];
    }
    return toggleKey ? can(toggleKey) : true;
  };

  // Resolve a flexbox `order` value for a section key. Sections not present
  // in the resolved layout fall to the bottom (order = 999) — admins can
  // pin a section's position by including it in the layout.
  const orderIndex = (layoutKey) => {
    const idx = layoutOrderKeys.indexOf(layoutKey);
    return idx === -1 ? 999 : idx;
  };

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        const venueRes = await axios.get(`${API}/venues/by-path/${districtSlug}/${areaSlug}/${venueSlug}`);
        setVenue(venueRes.data);
        // Reviews + events keyed on the resolved venue_id (not the path slug
        // — the previous code passed `venueSlug` to /api/venues/{id}/reviews
        // so it always returned an empty array).
        if (venueRes.data?.venue_id) {
          axios.get(`${API}/events?venue_id=${venueRes.data.venue_id}&limit=4`).then(({ data }) => setEvents(data || []));
          axios.get(`${API}/venues/${venueRes.data.venue_id}/reviews?limit=20`).then(({ data }) => setReviews(data || []));
          try {
            const menuRes = await axios.get(`${API}/venues/${venueRes.data.venue_id}/menu`);
            setMenuCategories(menuRes.data || []);
          } catch {}
          try {
            const promosRes = await axios.get(`${API}/venues/${venueRes.data.venue_id}/promotions`);
            setPromotions(promosRes.data || []);
          } catch {}
          // Opening hours and specialty chips — both feed gated sections in
          // the public profile (controlled by `show_opening_hours` and
          // `listing_visible` toggles via the layout resolver).
          try {
            const hoursRes = await axios.get(`${API}/venues/${venueRes.data.venue_id}/opening-hours`);
            setOpeningHours(hoursRes.data || []);
          } catch {}
          if ((venueRes.data.specialty_ids || []).length > 0) {
            try {
              const cuisinesRes = await axios.get(`${API}/cuisines`);
              const byId = Object.fromEntries((cuisinesRes.data || []).map((c) => [c.id, c]));
              setSpecialties(
                venueRes.data.specialty_ids
                  .map((id) => byId[id])
                  .filter(Boolean),
              );
            } catch {}
          }
          // Rating summary — cached on venues collection, refreshed by every
          // status change on a review.
          try {
            const sumRes = await axios.get(`${API}/venues/${venueRes.data.venue_id}/rating-summary`);
            setRatingSummary(sumRes.data);
          } catch {}
        }

        // Check if favorite
        if (isAuthenticated) {
          try {
            const favRes = await axios.get(`${API}/favorites`);
            setIsFavorite(favRes.data.some((v) => v.venue_id === venueRes.data.venue_id));
          } catch (e) {
            // Ignore
          }
        }

        // Pre-fill user data
        if (user) {
          setBookingForm((f) => ({
            ...f,
            name: user.name || "",
            email: user.email || "",
            phone: user.phone || "",
          }));
        }
      } catch (error) {
        console.error("Error fetching venue:", error);
        navigate("/venues");
      } finally {
        setLoading(false);
      }
    };
    fetchVenue();
  }, [districtSlug, areaSlug, venueSlug, isAuthenticated, user, navigate]);

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${venue.venue_id}`);
        setIsFavorite(false);
        toast.success(t("venue:removed_favorite", { defaultValue: "Removed from favorites" }));
      } else {
        await axios.post(`${API}/favorites/${venue.venue_id}`);
        setIsFavorite(true);
        toast.success(t("venue:added_favorite", { defaultValue: "Added to favorites" }));
      }
    } catch (error) {
      toast.error(t("venue:favorite_failed", { defaultValue: "Failed to update favorites" }));
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setBookingLoading(true);

    try {
      await axios.post(`${API}/bookings`, {
        venue_id: venue.venue_id,
        booking_date: format(bookingForm.date, "yyyy-MM-dd"),
        booking_time: bookingForm.time,
        guest_count: parseInt(bookingForm.guests),
        contact_name: bookingForm.name,
        contact_phone: bookingForm.phone,
        contact_email: bookingForm.email,
        special_request: bookingForm.specialRequest || null,
      });
      setBookingSuccess(true);
      toast.success(t("booking:submitted_toast", { defaultValue: "Booking submitted successfully!" }));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || t("booking:submit_failed", { defaultValue: "Failed to submit booking" }));
    } finally {
      setBookingLoading(false);
    }
  };

  const timeSlots = [
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
    "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30"
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 animate-pulse">
          <div className="max-w-7xl mx-auto px-4">
            <div className="bg-muted h-96 rounded-xl mb-8" />
            <div className="bg-muted h-8 w-1/3 rounded mb-4" />
            <div className="bg-muted h-4 w-1/2 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!venue) return null;

  const resolveImgUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${API.replace('/api', '')}${url}`;
    return url;
  };

  const allImages = [venue.hero_image, ...(venue.gallery_images || [])].filter(Boolean);
  const uniqueImages = [...new Set(allImages)].map(resolveImgUrl);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-20">
        {/* Photo Collage — gated by layout `gallery` + plan `photos_basic` */}
        {showSection("gallery", "photos_basic") && (
          <VenueHero images={uniqueImages} alt={venue.name} />
        )}

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Details. Switched from `space-y-8` to a flex
                column so each section can carry its own `order` value
                derived from the resolved layout (drag-reorder in
                /admin/layouts → re-renders the public profile in the new
                order without us touching JSX). */}
            <div className={`${venue.bookings_enabled !== false ? "lg:col-span-2" : "lg:col-span-3"} flex flex-col gap-8`}>
              {/* Header — pinned to the top of the left column (chrome,
                  not part of the layout-reorderable section list). */}
              <div style={{ order: -100 }}>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <span>{lang === "el" ? (venue.venue_type_name_el || venue.venue_type_name) : venue.venue_type_name}</span>
                  {venue.cuisine_name && (
                    <>
                      <span>•</span>
                      <span>{venue.cuisine_name}</span>
                    </>
                  )}
                  {/* price_range (€/€€/€€€/€€€€) was removed — user will
                      reintroduce it as a configurable tag_group later, so
                      every venue can be tagged via the unified taxonomy
                      instead of a hardcoded numeric scale. */}
                </div>

                <h1 className="text-3xl sm:text-4xl font-medium text-foreground tracking-tight" data-testid="venue-name">
                  {L("name")}
                </h1>

                <div className="flex items-center gap-4 mt-4">
                  {showSection("rating_summary_header", "can_show_rating_score") && ratingSummary?.total > 0 && (
                    <RatingChip summary={ratingSummary} />
                  )}
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {/* Tappable area · city: clicking jumps to the venues page
                        pre-filtered by city (and area, if set), so visitors
                        can quickly explore the same neighbourhood. */}
                    {venue.area_name ? (
                      <Link
                        to={`/venues?city_id=${encodeURIComponent(venue.city_id || "")}&area_id=${encodeURIComponent(venue.area_id || "")}`}
                        className="hover:text-primary transition-colors"
                        data-testid="venue-detail-area-pill"
                      >
                        <span>{lang === "el" ? (venue.area_name_el || venue.area_name) : venue.area_name}</span>
                        <span className="mx-1 opacity-60">·</span>
                        <span>{lang === "el" ? (venue.city_name_el || venue.city_name) : venue.city_name}</span>
                      </Link>
                    ) : (
                      <span>{lang === "el" ? (venue.city_name_el || venue.city_name) : venue.city_name}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleFavorite}
                    data-testid="venue-favorite-button"
                  >
                    <Heart className={`h-4 w-4 mr-2 ${isFavorite ? "fill-red-500 text-red-500" : ""}`} />
                    {isFavorite ? t("venue:saved", { defaultValue: "Saved" }) : t("venue:save", { defaultValue: "Save" })}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const shareData = {
                        title: venueName,
                        text: venueDescShort || venueName,
                        url: window.location.href,
                      };
                      if (navigator.share) {
                        try {
                          await navigator.share(shareData);
                        } catch (err) {
                          // User cancelled or share failed silently — fall back to clipboard
                          if (err?.name !== "AbortError") {
                            navigator.clipboard.writeText(window.location.href);
                            toast.success(t("venue:link_copied", { defaultValue: "Link copied!" }));
                          }
                        }
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success(t("venue:link_copied", { defaultValue: "Link copied!" }));
                      }
                    }}
                    data-testid="venue-share-button"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    {t("venue:share", { defaultValue: "Share" })}
                  </Button>
                </div>
              </div>

              {/* Claim this listing — visible only on Free Listing venues
                  with no assigned owner. Submits a public claim form which
                  the admin reviews from /admin/venue-claims. */}
              {venue.is_claimable && showSection("claim_banner") && (
                <div
                  className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center justify-between gap-4"
                  data-testid="venue-claim-banner"
                  style={{ order: orderIndex("claim_banner") }}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">
                      {t("venue:claim_title", { defaultValue: "Is this your venue?" })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("venue:claim_subtitle", {
                        defaultValue:
                          "Claim it to manage your profile, photos, menu and bookings on TableCy.",
                      })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setClaimOpen(true)}
                    data-testid="venue-claim-btn"
                  >
                    {t("venue:claim_btn", { defaultValue: "Claim this listing" })}
                  </Button>
                </div>
              )}
              <ClaimVenueDialog
                open={claimOpen}
                onOpenChange={setClaimOpen}
                venue={venue}
              />

              {/* Description — gated by layout `about` + plan `custom_description` */}
              {showSection("about", "custom_description") && (venue.description_long || venue.description_short) && (
                <div data-testid="venue-description" style={{ order: orderIndex("about") }}>
                  <h2 className="text-xl font-medium text-foreground mb-4">{t("venue:about")}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {L("description_long") || L("description_short")}
                  </p>
                </div>
              )}

              {/* Specialty chips — gated by layout `specialties` + `listing_visible` */}
              {showSection("specialties", "listing_visible") && specialties.length > 0 && (
                <div data-testid="venue-specialties" style={{ order: orderIndex("specialties") }}>
                  <h2 className="text-xl font-medium text-foreground mb-3">
                    {t("venue:specialties", { defaultValue: "Specialties" })}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {specialties.map((s) => (
                      <span
                        key={s.id}
                        className="px-3 py-1 rounded-full bg-muted text-sm text-foreground border border-border"
                        data-testid={`venue-specialty-${s.id}`}
                      >
                        {lang === "el" ? (s.name_el || s.name) : s.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Opening hours — gated by layout `hours` + plan `show_opening_hours` */}
              {showSection("hours", "show_opening_hours") && openingHours.length > 0 && (
                <div data-testid="venue-hours" style={{ order: orderIndex("hours") }}>
                  <h2 className="text-xl font-medium text-foreground mb-4">
                    {t("venue:hours", { defaultValue: "Opening hours" })}
                  </h2>
                  <ul className="divide-y divide-border bg-muted/30 rounded-xl overflow-hidden">
                    {openingHours
                      .slice()
                      .sort((a, b) => (a.day_of_week ?? 0) - (b.day_of_week ?? 0))
                      .map((h) => (
                        <li
                          key={h.day_of_week}
                          className="flex items-center justify-between px-4 py-2 text-sm"
                          data-testid={`venue-hours-day-${h.day_of_week}`}
                        >
                          <span className="font-medium text-foreground capitalize">
                            {t(`venue:day_${h.day_of_week}`, {
                              defaultValue: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][h.day_of_week ?? 0],
                            })}
                          </span>
                          <span className="text-muted-foreground tabular-nums">
                            {h.is_closed
                              ? t("venue:closed", { defaultValue: "Closed" })
                              : `${h.open_time || "—"} – ${h.close_time || "—"}`}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Contact / Map / Social — three independently gated sub-sections */}
              {(showSection("contact", "show_contact_info") ||
                showSection("social", "show_social_links") ||
                showSection("map", "show_location_pin")) && (
              <div data-testid="venue-contact" style={{ order: orderIndex("contact") }}>
                <h2 className="text-xl font-medium text-foreground mb-4">{t("venue:contact")}</h2>
                {showSection("contact", "show_contact_info") && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <MapPin className="h-5 w-5 text-primary" />
                    <span>{L("formatted_address") || L("address")}</span>
                  </div>
                  {venue.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-primary" />
                      <a href={`tel:${venue.phone}`} className="text-muted-foreground hover:text-primary">
                        {venue.phone}
                      </a>
                    </div>
                  )}
                  {venue.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <a href={`mailto:${venue.email}`} className="text-muted-foreground hover:text-primary">
                        {venue.email}
                      </a>
                    </div>
                  )}
                  {venue.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-primary" />
                      <a href={venue.website} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary">
                        {venue.website}
                      </a>
                    </div>
                  )}
                  {showSection("social", "show_social_links") && (venue.facebook_url || venue.instagram_url) && (
                    <div className="flex items-center gap-3 pt-1" data-testid="venue-social-links">
                      {venue.facebook_url && (
                        <a
                          href={venue.facebook_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Facebook"
                          className="w-9 h-9 rounded-full bg-muted hover:bg-primary/10 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                          data-testid="venue-facebook-link"
                        >
                          <Facebook className="h-4 w-4" />
                        </a>
                      )}
                      {venue.instagram_url && (
                        <a
                          href={venue.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Instagram"
                          className="w-9 h-9 rounded-full bg-muted hover:bg-primary/10 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                          data-testid="venue-instagram-link"
                        >
                          <Instagram className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* OpenStreetMap Mini Map — gated by `map` */}
                {showSection("map", "show_location_pin") && (
                  venue.latitude && venue.longitude ? (
                    <VenueMiniMap lat={venue.latitude} lng={venue.longitude} name={venue.name} />
                  ) : (
                    <div className="mt-6 h-64 bg-muted rounded-xl flex items-center justify-center" data-testid="venue-map-placeholder">
                      <div className="text-center text-muted-foreground">
                        <MapPin className="h-8 w-8 mx-auto mb-2" />
                        <p>{t("venue:location")}</p>
                      </div>
                    </div>
                  )
                )}
              </div>
              )}

              {/* Promotions — gated by layout `promotions` + plan `news_posts` */}
              {showSection("promotions", "news_posts") && promotions.length > 0 && (
                <div data-testid="venue-promotions" style={{ order: orderIndex("promotions") }}>
                  <h2 className="text-xl font-medium text-foreground mb-4 flex items-center gap-2">
                    <Megaphone className="h-5 w-5 text-primary" />
                    Special Offers
                  </h2>
                  <div className="space-y-3">
                    {promotions.map(promo => {
                      const promoTitle = lang === "el" ? (promo.title_el || promo.title) : promo.title;
                      const promoDesc = lang === "el" ? (promo.description_el || promo.description) : promo.description;
                      const promoDiscount = lang === "el" ? (promo.discount_text_el || promo.discount_text) : promo.discount_text;
                      return (
                      <div key={promo.promo_id} className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4" data-testid={`venue-promo-${promo.promo_id}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
                            <Megaphone className="h-5 w-5 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-foreground">{promoTitle}</span>
                              {promoDiscount && <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-medium">{promoDiscount}</span>}
                            </div>
                            {promoDesc && <p className="text-sm text-muted-foreground mt-1">{promoDesc}</p>}
                            <p className="text-xs text-muted-foreground mt-1.5">
                              {promo.start_date} - {promo.end_date}
                              {promo.start_time && promo.end_time && ` \u2022 ${promo.start_time} - ${promo.end_time}`}
                            </p>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Menu */}
              {showSection("menu", "menu_module") && (
                <VenueMenu categories={menuCategories} lang={lang} order={orderIndex("menu")} />
              )}

              {/* Events */}
              {showSection("events", "events_module") && events.length > 0 && (
                <div data-testid="venue-events" style={{ order: orderIndex("events") }}>
                  <h2 className="text-xl font-medium text-foreground mb-4">Upcoming Events</h2>
                  <div className="space-y-4">
                    {events.map((event) => (
                      <Link
                        key={event.event_id}
                        to={`/event/${event.slug}`}
                        className="flex gap-4 p-4 bg-muted rounded-xl hover:bg-muted/80 transition-colors"
                      >
                        {event.image_url && (
                          <img
                            src={event.image_url}
                            alt={event.title}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div>
                          <p className="text-sm text-primary font-medium">
                            {formatDate(event.start_datetime, "EEE, MMM d 'at' h:mm a", i18n.language)}
                          </p>
                          <h3 className="font-medium text-foreground">{event.title}</h3>
                          {event.description_short && (
                            <p className="text-sm text-muted-foreground line-clamp-1">{event.description_short}</p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Reviews — fully composable from layout-gated blocks. Each
                  child component (header score, distribution, submission
                  form, public list, owner replies) is independently switchable
                  in the admin Layout builder so a Verified plan can show only
                  the score, while Booking shows the full UI. */}
              <VenueReviews
                venue={venue}
                summary={ratingSummary}
                reviews={reviews}
                lang={i18n.language}
                t={t}
                showSection={showSection}
                order={orderIndex("reviews_section")}
                onChanged={({ reviews: r, summary: s }) => {
                  if (r) setReviews(r);
                  if (s) setRatingSummary(s);
                }}
              />
            </div>

            {/* Right Column - Booking Widget — gated by layout `booking` + plan `bookings_enabled` */}
            {showSection("booking", "bookings_enabled") ? (
            <div className="lg:col-span-1">
              <div className="sticky top-24 glass rounded-2xl p-6 border border-border" data-testid="booking-widget">
                <h3 className="text-lg font-medium text-foreground mb-4">{t("booking:book_now")}</h3>

                {bookingSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <h4 className="text-lg font-medium text-foreground mb-2">{t("booking:submitted_title", { defaultValue: "Booking Submitted!" })}</h4>
                    <p className="text-muted-foreground text-sm mb-4">
                      {venue.booking_mode === "instant_confirm"
                        ? t("booking:confirmed_message", { defaultValue: "Your reservation has been confirmed." })
                        : t("booking:pending_message", { defaultValue: "We'll confirm your reservation shortly." })}
                    </p>
                    <Button onClick={() => setBookingSuccess(false)} variant="outline" className="w-full">
                      {t("booking:book_now")}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleBooking} className="space-y-4">
                    <div>
                      <Label className="text-sm">{t("booking:date")}</Label>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full justify-start mt-1" data-testid="booking-date-trigger">
                            <CalendarIcon className="h-4 w-4 mr-2" />
                            {formatDate(bookingForm.date, "EEE, MMM d, yyyy", i18n.language)}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle>{t("booking:select_date")}</DialogTitle>
                          </DialogHeader>
                          <Calendar
                            mode="single"
                            selected={bookingForm.date}
                            onSelect={(date) => date && setBookingForm((f) => ({ ...f, date }))}
                            disabled={(date) => date < new Date()}
                            className="rounded-md border"
                          />
                        </DialogContent>
                      </Dialog>
                    </div>

                    <div>
                      <Label className="text-sm">{t("booking:time")}</Label>
                      <Select
                        value={bookingForm.time}
                        onValueChange={(v) => setBookingForm((f) => ({ ...f, time: v }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="booking-time-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">{t("booking:guests")}</Label>
                      <Select
                        value={bookingForm.guests}
                        onValueChange={(v) => setBookingForm((f) => ({ ...f, guests: v }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="booking-guests-select">
                          <Users className="h-4 w-4 mr-2" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <SelectItem key={n} value={n.toString()}>
                              {t("booking:guests_count", { count: n })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="booking-name" className="text-sm">{t("booking:name")}</Label>
                      <Input
                        id="booking-name"
                        value={bookingForm.name}
                        onChange={(e) => setBookingForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder={t("booking:name_placeholder", { defaultValue: "Your name" })}
                        required
                        className="mt-1"
                        data-testid="booking-name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="booking-phone" className="text-sm">{t("booking:phone")}</Label>
                      <Input
                        id="booking-phone"
                        type="tel"
                        value={bookingForm.phone}
                        onChange={(e) => setBookingForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder="+357 99 123456"
                        required
                        className="mt-1"
                        data-testid="booking-phone-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="booking-email" className="text-sm">{t("booking:email")}</Label>
                      <Input
                        id="booking-email"
                        type="email"
                        value={bookingForm.email}
                        onChange={(e) => setBookingForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="you@example.com"
                        required
                        className="mt-1"
                        data-testid="booking-email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="booking-request" className="text-sm">{t("booking:special_request")}</Label>
                      <Textarea
                        id="booking-request"
                        value={bookingForm.specialRequest}
                        onChange={(e) => setBookingForm((f) => ({ ...f, specialRequest: e.target.value }))}
                        placeholder={t("booking:special_request_placeholder")}
                        className="mt-1"
                        rows={3}
                        data-testid="booking-request-input"
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={bookingLoading} data-testid="booking-submit-button">
                      {bookingLoading ? t("common:loading") : t("booking:submit")}
                    </Button>

                    {venue.booking_mode === "request_only" && (
                      <p className="text-xs text-muted-foreground text-center">
                        {t("booking:requires_confirmation")}
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
            ) : null}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}


function VenueMiniMap({ lat, lng, name }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    let cancelled = false;

    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapRef.current) return;

      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
        iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
        shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
      });

      const map = L.map(mapRef.current, { scrollWheelZoom: false, zoomControl: true }).setView([lat, lng], 15);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);
      L.marker([lat, lng]).addTo(map).bindPopup(`<strong>${name}</strong>`).openPopup();
      mapInstanceRef.current = map;
    })();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [lat, lng, name]);

  const gmapsHref = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <div className="mt-6" data-testid="venue-mini-map">
      <div ref={mapRef} className="h-64 rounded-xl overflow-hidden border border-border" style={{ zIndex: 0 }} />
      <a
        href={gmapsHref}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity text-sm font-medium"
        data-testid="venue-google-maps-link"
      >
        <MapPin className="h-4 w-4" />
        {t("venue:open_in_google_maps", { defaultValue: "Open in Google Maps" })}
      </a>
    </div>
  );
}


/* ============== CLAIM DIALOG (Phase 3) ============== */
function ClaimVenueDialog({ open, onOpenChange, venue }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_role: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Reset state every time the dialog is reopened so the success
  // confirmation doesn't stick around for the next claim attempt.
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setForm({ name: "", email: "", phone: "", business_role: "", message: "" });
    }
  }, [open]);

  if (!venue) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/venues/${venue.venue_id}/claim`, form);
      setSubmitted(true);
      toast.success(
        t("venue:claim_submitted_toast", { defaultValue: "Claim submitted — we'll be in touch shortly." }),
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="venue-claim-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("venue:claim_dialog_title", { defaultValue: "Claim {{name}}", name: venue.name })}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-6 space-y-3" data-testid="claim-success">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="h-7 w-7 text-green-600" />
            </div>
            <p className="font-medium text-foreground">
              {t("venue:claim_success_title", { defaultValue: "Thanks — we got it!" })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("venue:claim_success_body", {
                defaultValue:
                  "Our team will verify your claim and email you within 1–2 business days.",
              })}
            </p>
            <Button onClick={() => onOpenChange(false)} className="w-full">
              {t("venue:close", { defaultValue: "Close" })}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="claim-form">
            <p className="text-sm text-muted-foreground">
              {t("venue:claim_form_intro", {
                defaultValue:
                  "Tell us a bit about your role at the venue and we'll review your claim.",
              })}
            </p>
            <div>
              <Label>{t("venue:claim_field_name", { defaultValue: "Your full name" })} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                required className="mt-1" data-testid="claim-name" />
            </div>
            <div>
              <Label>{t("venue:claim_field_email", { defaultValue: "Email" })} *</Label>
              <Input type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required className="mt-1" data-testid="claim-email" />
            </div>
            <div>
              <Label>{t("venue:claim_field_phone", { defaultValue: "Phone" })}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1" data-testid="claim-phone" />
            </div>
            <div>
              <Label>{t("venue:claim_field_role", { defaultValue: "Your role" })}</Label>
              <Input value={form.business_role}
                onChange={(e) => setForm({ ...form, business_role: e.target.value })}
                className="mt-1" placeholder="Owner / Manager / Authorised representative"
                data-testid="claim-role" />
            </div>
            <div>
              <Label>{t("venue:claim_field_message", { defaultValue: "Message (optional)" })}</Label>
              <Textarea value={form.message} rows={3}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1" data-testid="claim-message" />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="claim-submit">
              {loading
                ? t("venue:claim_submitting", { defaultValue: "Submitting..." })
                : t("venue:claim_submit", { defaultValue: "Submit claim" })}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
